import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  ClipboardCheck, AlertTriangle, Clock, CheckCircle2, RefreshCw, X,
  ShieldAlert, Info, CheckCircle, Eye, XCircle, MapPin, UserCheck, Send
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SEV = {
  critical: { label: "Critical", color: "bg-red-50 text-red-700", icon: AlertTriangle },
  high:     { label: "High",     color: "bg-orange-50 text-orange-700", icon: ShieldAlert },
  medium:   { label: "Medium",   color: "bg-amber-50 text-amber-700", icon: Info },
  low:      { label: "Low",      color: "bg-green-50 text-green-700", icon: CheckCircle },
};

const STATUS = {
  issued:               { label: "Issued — Awaiting Response", color: "bg-slate-100 text-slate-600" },
  in_progress:          { label: "In Progress",       color: "bg-blue-50 text-blue-700" },
  completed_by_insured: { label: "Awaiting Review",   color: "bg-amber-50 text-amber-700" },
  disputed:             { label: "Disputed",          color: "bg-red-50 text-red-700" },
  closed:               { label: "Closed",            color: "bg-green-50 text-green-700" },
};

function DetailModal({ rec, onClose, onAction }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const st = STATUS[rec.status] || STATUS.issued;
  const sev = SEV[rec.severity] || SEV.medium;

  const act = async (action) => {
    setBusy(true);
    await onAction(rec.id, action, note);
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" data-testid="rec-detail-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sev.color}`}>{sev.label}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
          </div>
          <button onClick={onClose} data-testid="close-rec-modal-btn" className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-800">{rec.title}</h2>
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
              <MapPin className="w-3 h-3" /> {rec.insured_name} · {rec.property_address} {rec.policy_number ? `· ${rec.policy_number}` : ""}
            </p>
          </div>
          <p className="text-sm text-slate-600">{rec.description}</p>
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-xs font-semibold text-blue-700 mb-1">Recommended Action</p>
            <p className="text-sm text-blue-800">{rec.recommendation}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs text-slate-500">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="font-semibold text-slate-600">Issued</p>
              <p>{rec.issued_at ? new Date(rec.issued_at).toLocaleDateString() : "—"}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="font-semibold text-slate-600">Response Due</p>
              <p>{rec.due_date ? new Date(rec.due_date).toLocaleDateString() : "—"}</p>
            </div>
          </div>

          {rec.response && (
            <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl" data-testid="rec-insured-response">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-1.5">Insured Response — {rec.response.type?.replace("_", " ")}</p>
              {rec.response.note && <p className="text-sm text-slate-700 italic">&ldquo;{rec.response.note}&rdquo;</p>}
              {rec.response.target_date && <p className="text-xs text-slate-500 mt-1">Target date: {rec.response.target_date}</p>}
              <p className="text-xs text-slate-400 mt-1">Responded {rec.response.responded_at ? new Date(rec.response.responded_at).toLocaleDateString() : ""}</p>
            </div>
          )}

          {(rec.proof_photos || []).length > 0 && (
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Proof Photos</p>
              <div className="flex gap-2 flex-wrap">
                {rec.proof_photos.map((u, i) => (
                  <a key={i} href={`${BACKEND_URL}${u}`} target="_blank" rel="noreferrer">
                    <img src={`${BACKEND_URL}${u}`} alt="Proof" className="w-24 h-24 object-cover rounded-lg border border-slate-200 hover:opacity-80" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {(rec.history || []).length > 0 && (
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">History</p>
              <div className="space-y-1.5">
                {rec.history.slice().reverse().map((h, i) => (
                  <p key={i} className="text-xs text-slate-500">
                    <span className="font-semibold text-slate-600">{h.action?.replace(/_/g, " ")}</span> — {h.by}{h.note ? ` · "${h.note}"` : ""} · {h.at ? new Date(h.at).toLocaleDateString() : ""}
                  </p>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-slate-100 pt-4">
            <textarea
              data-testid="rec-action-note-input"
              value={note} onChange={e => setNote(e.target.value)} rows={2}
              placeholder="Closure / reopen note (optional)"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-navy resize-none mb-3"
            />
            <div className="flex items-center gap-2 flex-wrap">
              {rec.status !== "closed" ? (
                <>
                  <button data-testid="close-rec-btn" disabled={busy} onClick={() => act("close")}
                    className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-50">
                    <CheckCircle2 className="w-4 h-4" /> Verify & Close
                  </button>
                  <button data-testid="request-field-verify-btn" disabled={busy} onClick={() => act("field")}
                    className="flex items-center gap-1.5 px-4 py-2 bg-navy/10 text-navy rounded-lg text-sm font-semibold hover:bg-navy hover:text-white transition-colors disabled:opacity-50">
                    <UserCheck className="w-4 h-4" /> Request Field Verification
                  </button>
                </>
              ) : (
                <button data-testid="reopen-rec-btn" disabled={busy} onClick={() => act("reopen")}
                  className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-600 hover:text-white transition-colors disabled:opacity-50">
                  <XCircle className="w-4 h-4" /> Reopen
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RecommendationsDashboard() {
  const [recs, setRecs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [sevFilter, setSevFilter] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [detail, setDetail] = useState(null);

  const loadIdRef = React.useRef(0);
  const load = useCallback(async () => {
    const myId = ++loadIdRef.current;
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (sevFilter) params.severity = sevFilter;
      if (overdueOnly) params.overdue = true;
      const [recsRes, statsRes] = await Promise.all([
        api.get("/recs", { params }),
        api.get("/recs/stats"),
      ]);
      if (myId !== loadIdRef.current) return;
      setRecs(recsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      if (myId === loadIdRef.current) toast.error("Failed to load recommendations");
    }
    if (myId === loadIdRef.current) setLoading(false);
  }, [statusFilter, sevFilter, overdueOnly]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (recId, action, note) => {
    try {
      if (action === "close") await api.put(`/recs/${recId}/close`, { note });
      else if (action === "reopen") await api.put(`/recs/${recId}/reopen`, { note });
      else if (action === "field") await api.post(`/recs/${recId}/request-field-verification`);
      toast.success(action === "field" ? "Routed to LC dispatch queue" : `Recommendation ${action === "close" ? "closed" : "reopened"}`);
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const nowIso = new Date().toISOString();
  const isOverdue = (r) => !["closed", "completed_by_insured"].includes(r.status) && r.due_date && r.due_date < nowIso;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="recs-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-800">Recommendations</h1>
            <p className="text-slate-500 text-sm mt-0.5">Track corrective actions from issuance to insured response</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: "Total Issued", value: stats.total, color: "text-slate-700", tid: "stat-total" },
              { label: "Open", value: stats.open, color: "text-navy", tid: "stat-open" },
              { label: "Overdue", value: stats.overdue, color: "text-red-600", tid: "stat-overdue" },
              { label: "Awaiting Review", value: stats.awaiting_review, color: "text-amber-600", tid: "stat-awaiting" },
              { label: "Compliance Rate", value: `${stats.compliance_rate}%`, color: "text-green-600", tid: "stat-compliance" },
            ].map(s => (
              <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={s.tid}>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">{s.label}</p>
                <p className={`font-heading text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <select data-testid="rec-status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-navy">
            <option value="">All Statuses</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select data-testid="rec-sev-filter" value={sevFilter} onChange={e => setSevFilter(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-navy">
            <option value="">All Severities</option>
            {Object.entries(SEV).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" data-testid="rec-overdue-filter" checked={overdueOnly} onChange={e => setOverdueOnly(e.target.checked)}
              className="rounded border-slate-300" />
            Overdue only
          </label>
          <button onClick={load} data-testid="rec-refresh-btn"
            className="ml-auto w-8 h-8 flex items-center justify-center border border-slate-200 rounded-lg text-slate-400 hover:text-navy hover:border-navy transition-colors">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="py-16 text-center"><RefreshCw className="w-6 h-6 text-navy animate-spin mx-auto" /></div>
          ) : recs.length === 0 ? (
            <div className="py-16 text-center">
              <ClipboardCheck className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">No recommendations match your filters</p>
              <p className="text-slate-300 text-xs mt-1">Recommendations are created automatically when reports are finalized</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-400 uppercase tracking-wider border-b border-slate-100">
                    <th className="px-4 py-3 font-semibold">Recommendation</th>
                    <th className="px-4 py-3 font-semibold hidden md:table-cell">Insured</th>
                    <th className="px-4 py-3 font-semibold">Severity</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold hidden lg:table-cell">Due</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recs.map(r => {
                    const sev = SEV[r.severity] || SEV.medium;
                    const st = STATUS[r.status] || STATUS.issued;
                    const od = isOverdue(r);
                    return (
                      <tr key={r.id} className="hover:bg-slate-50 transition-colors" data-testid={`rec-row-${r.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-800">{r.title}</p>
                          <p className="text-xs text-slate-400 mt-0.5 md:hidden">{r.insured_name}</p>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-slate-600">{r.insured_name}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sev.color}`}>{sev.label}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                          {od && <span className="ml-1.5 text-xs font-bold text-red-600 inline-flex items-center gap-0.5"><Clock className="w-3 h-3" /> Overdue</span>}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500">
                          {r.due_date ? new Date(r.due_date).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button data-testid={`view-rec-btn-${r.id}`} onClick={() => setDetail(r)}
                            className="flex items-center gap-1 px-2.5 py-1 bg-navy/10 text-navy hover:bg-navy hover:text-white rounded-md text-xs font-medium transition-colors ml-auto">
                            <Eye className="w-3 h-3" /> Review
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {detail && <DetailModal rec={detail} onClose={() => setDetail(null)} onAction={handleAction} />}
    </Layout>
  );
}
